import type {
  ThreadAssistantMessagePart,
  ThreadUserMessagePart,
  ToolApprovalResponse,
  ToolCallMessagePartStatus,
  Unstable_ToolInteractionInput,
} from "../../types/message";
import { resolveToolApprovalResponse } from "../utils/resolveToolApprovalResponse";
import { createToolInteraction } from "../utils/tool-interactions";
import type { Unsubscribe } from "../../types/unsubscribe";
import type { MessagePartStatus } from "../../types/message";
import type { SubscribableWithState } from "../../subscribable/subscribable";
import type { ThreadRuntimeCoreBinding } from "./thread-runtime";
import type { MessageStateBinding } from "./bindings";
import type { MessagePartRuntimePath } from "./paths";
import { ToolResponse } from "assistant-stream";

export type MessagePartState = (
  | ThreadUserMessagePart
  | ThreadAssistantMessagePart
) & {
  readonly status: MessagePartStatus | ToolCallMessagePartStatus;
};

type MessagePartSnapshotBinding = SubscribableWithState<
  MessagePartState,
  MessagePartRuntimePath
>;

export type MessagePartRuntime = {
  addToolResult(result: any | ToolResponse<any>): void;
  resumeToolCall(payload: unknown): void;
  respondToToolApproval(response: ToolApprovalResponse): Promise<void>;
  unstable_recordInteraction?: (
    input: Unstable_ToolInteractionInput,
  ) => Promise<void>;

  readonly path: MessagePartRuntimePath;
  getState(): MessagePartState;
  subscribe(callback: () => void): Unsubscribe;
};

export class MessagePartRuntimeImpl implements MessagePartRuntime {
  public get path() {
    return this.contentBinding.path;
  }

  private contentBinding: MessagePartSnapshotBinding;
  private messageApi: MessageStateBinding | undefined;
  private threadApi: ThreadRuntimeCoreBinding | undefined;

  constructor(
    contentBinding: MessagePartSnapshotBinding,
    messageApi?: MessageStateBinding,
    threadApi?: ThreadRuntimeCoreBinding,
  ) {
    this.contentBinding = contentBinding;
    this.messageApi = messageApi;
    this.threadApi = threadApi;
    this.__internal_bindMethods();
  }

  protected __internal_bindMethods() {
    this.addToolResult = this.addToolResult.bind(this);
    this.resumeToolCall = this.resumeToolCall.bind(this);
    this.respondToToolApproval = this.respondToToolApproval.bind(this);
    this.unstable_recordInteraction =
      this.unstable_recordInteraction.bind(this);
    this.getState = this.getState.bind(this);
    this.subscribe = this.subscribe.bind(this);
  }

  public getState() {
    return this.contentBinding.getState();
  }

  public addToolResult(result: any | ToolResponse<any>) {
    const state = this.contentBinding.getState();
    if (!state) throw new Error("Message part is not available");

    if (state.type !== "tool-call")
      throw new Error("Tried to add tool result to non-tool message part");

    if (!this.messageApi)
      throw new Error(
        "Message API is not available. This is likely a bug in assistant-ui.",
      );
    if (!this.threadApi) throw new Error("Thread API is not available");

    const message = this.messageApi.getState();
    if (!message) throw new Error("Message is not available");

    const toolName = state.toolName;
    const toolCallId = state.toolCallId;

    const response = ToolResponse.toResponse(result);
    this.threadApi.getState().addToolResult({
      messageId: message.id,
      toolName,
      toolCallId,
      result: response.result,
      isError: response.isError,
      ...(response.artifact !== undefined && { artifact: response.artifact }),
      ...(response.modelContent !== undefined && {
        modelContent: response.modelContent,
      }),
    });
  }

  public resumeToolCall(payload: unknown) {
    const state = this.contentBinding.getState();
    if (!state) throw new Error("Message part is not available");

    if (state.type !== "tool-call")
      throw new Error("Tried to resume tool call on non-tool message part");

    if (!this.threadApi) throw new Error("Thread API is not available");

    const toolCallId = state.toolCallId;
    this.threadApi.getState().resumeToolCall({
      toolCallId,
      payload,
    });
    void this.unstable_recordInteraction({
      type: "human-response",
      payload,
    }).catch(() => {});
  }

  public respondToToolApproval(response: ToolApprovalResponse): Promise<void> {
    const state = this.contentBinding.getState();
    if (!state) throw new Error("Message part is not available");

    if (state.type !== "tool-call")
      throw new Error(
        "Tried to respond to tool approval on non-tool message part",
      );

    if (
      !state.approval ||
      state.approval.approved !== undefined ||
      state.approval.resolution !== undefined
    )
      throw new Error("Tool call has no pending approval");

    if (!this.threadApi) throw new Error("Thread API is not available");

    return this.threadApi
      .getState()
      .respondToToolApproval(
        resolveToolApprovalResponse(state.approval, response),
      );
  }

  public async unstable_recordInteraction(
    input: Unstable_ToolInteractionInput,
  ): Promise<void> {
    const state = this.contentBinding.getState();
    if (!state) throw new Error("Message part is not available");

    if (state.type !== "tool-call")
      throw new Error("Tried to record interaction on non-tool message part");

    if (!this.messageApi)
      throw new Error(
        "Message API is not available. This is likely a bug in assistant-ui.",
      );
    if (!this.threadApi) throw new Error("Thread API is not available");

    const message = this.messageApi.getState();
    if (!message) throw new Error("Message is not available");

    const core = this.threadApi.getState();
    if (!core.unstable_recordToolInteraction)
      throw new Error("Runtime does not support recording tool interactions.");

    await core.unstable_recordToolInteraction({
      messageId: message.id,
      toolCallId: state.toolCallId,
      interaction: createToolInteraction(input),
    });
  }

  public subscribe(callback: () => void) {
    return this.contentBinding.subscribe(callback);
  }
}
